import { httpService } from './http.service.js'
import { userService } from './user.service.js'
import { utilService } from './util.service.js'

const BASE_URL = 'board/'

export const boardService = {
    query,
    getById,
    getFilteredBoard,
    save,
    remove,
    getDefaultFilterBoard,
    getDefaultFilterBoards,
    getFilterFromSearchParams,
    getEmptyGroup,
    getEmptyTask,
    getEmptyComment,
    getEmptyActivity,
    getEmptyBoard,
    updateTask,
    updateGroup,
    addActivity,
    joinBoard
}

function query(filter = getDefaultFilterBoards()) {
    const queryParams = `?title=${filter.title}&isStarred=${filter.isStarred}`
    return httpService.get(BASE_URL + queryParams)
}

function getFilteredBoard(board, filterBy = getDefaultFilterBoard()) {
    // No active filter: return a shallow copy that still SHARES the groups/tasks
    // arrays with the store board. Optimistic updates (add task, change color, etc.)
    // mutate board.groups in place, and rely on that sharing to render immediately.
    if (!filterBy.title && !filterBy.memberId) return { ...board }

    // Active filter: deep-clone first so filtering never mutates the original board
    // (otherwise clearing the filter would not bring the filtered-out tasks back).
    const filteredBoard = structuredClone(board)
    if (filterBy.title) {
        const regex = new RegExp(filterBy.title, 'i')
        filteredBoard.groups.forEach(group => {
            group.tasks = group.tasks.filter(task => regex.test(task.title))
        })
    }
    if (filterBy.memberId) {
        // People can live in the legacy task.memberIds OR in any Person column (task.cmpVals[i]).
        const memberCols = (board.cmpsOrder || []).map((c, i) => (c === 'member-picker' ? i : -1)).filter(i => i >= 0)
        const has = task => (task.memberIds || []).includes(filterBy.memberId)
            || memberCols.some(i => Array.isArray(task.cmpVals && task.cmpVals[i]) && task.cmpVals[i].includes(filterBy.memberId))
        filteredBoard.groups.forEach(group => {
            group.tasks = group.tasks.filter(has)
        })
    }
    return filteredBoard
}

function getById(boardId) {
    return httpService.get(BASE_URL + boardId)
}

function remove(boardId) {
    return httpService.delete(BASE_URL + boardId)
}

function save(board) {
    if (board._id) return httpService.put(BASE_URL + board._id, board)
    return httpService.post(BASE_URL, board)
}

function updateTask(boardId, groupId, task) {
    return httpService.put(`${BASE_URL}${boardId}/${groupId}/${task.id}`, task)
}

function updateGroup(boardId, group) {
    return httpService.put(`${BASE_URL}${boardId}/${group.id}`, group)
}

// Append one activity-log entry — a tiny write, not a full-board save.
function addActivity(boardId, activity) {
    return httpService.put(`${BASE_URL}${boardId}/activity`, activity)
}

// Join a board via the invite link (adds the person to its members, deduped server-side).
function joinBoard(boardId, member) {
    return httpService.put(`${BASE_URL}${boardId}/member`, member)
}

function getDefaultFilterBoards() {
    return {
        title: '',
        isStarred: false
    }
}

function getDefaultFilterBoard() {
    return {
            title: '',
            memberId: '' 
        }
}

function getFilterFromSearchParams(searchParams) {
    const emptyFilter = getDefaultFilterBoard()
    const filterBy = {}
    for (const field in emptyFilter) {
        filterBy[field] = searchParams.get(field) || ''
    }
    return filterBy
}

function getEmptyGroup() {
    return {
        "id": utilService.makeId(),
        "title": 'New Group',
        "archivedAt": Date.now(),
        "tasks": [],
        "color": '#ffcb00',
    }
}

function getEmptyTask() {
    return {
        "id": utilService.makeId(),
        "title": "",
        "status": "",
        "priority": "",
        "memberIds": [],
        "dueDate": '',
        "comments": [],
        "updatedBy":{
            "imgUrl":"",
        },
        "file": "",
        // Per-COLUMN values: every column (status, date, number, text, …) stores its value
        // here keyed by its column index, so two columns of the same type never share a value.
        "cmpVals": {},
    }
}

// Maps a column type to its legacy flat task field (back-compat with existing/demo data
// that predates per-column storage). Only the FIRST column of a type reads this fallback.
export const LEGACY_FIELD = {
    'status-picker': 'status',
    'priority-picker': 'priority',
    'member-picker': 'memberIds',
    'date-picker': 'dueDate',
    'number-picker': 'number',
    'file-picker': 'file',
    'text-picker': 'text',
    'link-picker': 'link',
    'checkbox-picker': 'checkbox',
    'dropdown-picker': 'dropdown',
    'timeline-picker': 'timeline',
}

// SINGLE SOURCE OF TRUTH for reading a column's value for one row.
// 1) Prefer the per-column value at task.cmpVals[cmpIdx].
// 2) Otherwise, only the FIRST column of this type falls back to the legacy flat field
//    (task.status, task.dueDate, …) — so an unset 2nd/3rd column NEVER echoes another's value.
export function getCmpVal(task, board, cmpType, cmpIdx) {
    if (task && task.cmpVals && task.cmpVals[cmpIdx] != null) return task.cmpVals[cmpIdx]
    const firstIdx = (board && board.cmpsOrder) ? board.cmpsOrder.indexOf(cmpType) : -1
    if (cmpIdx === firstIdx && LEGACY_FIELD[cmpType]) return task[LEGACY_FIELD[cmpType]]
    return undefined
}

// Per-column width — points at a per-column CSS variable (--cw-<idx>) so the live width can be
// driven straight from the DOM during a resize drag (no React re-render of every row).
// The saved value is applied to that variable in board-details; here we just reference it.
export function colWidthVar(board, idx, cmpWidths) {
    const def = (board.cmpsOrder && board.cmpsOrder[idx] === 'member-picker') ? 87 : 140
    return `var(--cw-${idx}, ${def}px)`
}

// Human-readable string for a column value (used by filter dropdowns, export, etc.).
export function formatCmpValue(cmpType, val, board) {
    if (val == null || val === '') return ''
    if (cmpType === 'member-picker') return (Array.isArray(val) ? val : []).map(id => ((board && board.members) || []).find(m => m._id === id)?.fullname || '').filter(Boolean).join('; ')
    if (cmpType === 'date-picker') { const d = new Date(val); return isNaN(d) ? '' : d.toLocaleDateString() }
    if (cmpType === 'timeline-picker') { const s = val.start ? new Date(val.start).toLocaleDateString() : ''; const e = val.end ? new Date(val.end).toLocaleDateString() : ''; return [s, e].filter(Boolean).join(' - ') }
    if (cmpType === 'checkbox-picker') return val ? 'Yes' : ''
    if (Array.isArray(val)) return val.join('; ')
    return String(val)
}

// Distinct, individual value options present in a board column (for the filter Value dropdown).
// col = { key:'title' } for the task name, or { key:<idx>, cmp:<type> } for a data column.
export function getColumnValueOptions(board, col) {
    const out = new Set()
    const tasks = (board.groups || []).flatMap(g => g.tasks || [])
    for (const t of tasks) {
        if (col.key === 'title') { if (t.title) out.add(t.title); continue }
        const raw = getCmpVal(t, board, col.cmp, col.key)
        if (raw == null || raw === '') continue
        if (col.cmp === 'member-picker') (Array.isArray(raw) ? raw : []).forEach(id => { const n = ((board.members) || []).find(m => m._id === id)?.fullname; if (n) out.add(n) })
        else if (col.cmp === 'dropdown-picker' && Array.isArray(raw)) raw.forEach(tag => out.add(tag))
        else out.add(formatCmpValue(col.cmp, raw, board))
    }
    return [...out].filter(Boolean).sort()
}

function getEmptyComment() {
    return {
        "archivedAt": Date.now(),
        "byMember": {
            "_id": null,
            "fullname": "Guest",
            "imgUrl": "https://res.cloudinary.com/du63kkxhl/image/upload/v1675013009/guest_f8d60j.png"
        }, "txt": "",
        "style": {
            "textDecoration": "none",
            "fontWeight": "normal",
            "fontStyle": "normal",
            "textAlign": "Left"
        }
    }
}

function getEmptyActivity() {
    return {
        "action": "status",
        "createdAt": Date.now(),
        "byMember": userService.getLoggedinUser() || {
            "_id": null,
            "fullname": "Guest",
            "imgUrl": "https://res.cloudinary.com/du63kkxhl/image/upload/v1675013009/guest_f8d60j.png"
        },
        "task": {
            "id": "c101",
            "title": "Replace Logo"
        },
        "from": {}, 
        "to": {}
    }
}

function getEmptyBoard() {
    return {
        "title": 'New Board',
        "archivedAt": Date.now(),
        "isStarred": false,
        "createdBy":{
            "fullname":"Alex Nacul",
            "imgUrl":"https://files.monday.com/use1/photos/104769303/thumb/104769303-user_photo_initials_2026_06_03_09_29_49.png?1780478989",
            "_id": "104769303"
        },
        "labels": [
            {
                "id": "l101",
                "title": "Done",
                "color": "#00c875"
            },
            {
                "id": "l102",
                "title": "Progress",
                "color": "#fdab3d"
            },
            {
                "id": "l103",
                "title": "Stuck",
                "color": "#e2445c"
            },
            {
                "id": "l104",
                "title": "Low",
                "color": "#ffcb00"
            },
            {
                "id": "l105",
                "title": "Medium",
                "color": "#a25ddc"
            },
            {
                "id": "l106",
                "title": "High",
                "color": "#e2445c"
            },
            {
                "id": "l107",
                "title": "",
                "color": "#c4c4c4"
            },
        ],
        "members": [
            { "_id": "50857319", "fullname": "Thomas Markou", "imgUrl": "https://files.monday.com/use1/photos/50857319/thumb/50857319-user_photo_2023_10_26_08_28_20.png?1698308900" },
            { "_id": "68782354", "fullname": "Ruben", "imgUrl": "https://files.monday.com/use1/photos/68782354/thumb/68782354-user_photo_2024_11_20_14_24_52.png?1732112692" },
            { "_id": "35480373", "fullname": "Adela Zeka", "imgUrl": "https://files.monday.com/use1/photos/35480373/thumb/35480373-user_photo_initials_2022_10_11_09_10_15.png?1665479415" },
            { "_id": "42848022", "fullname": "Kate Zemouri", "imgUrl": "https://files.monday.com/use1/photos/42848022/thumb/42848022-user_photo_2023_05_08_07_43_23.png?1683531803" },
            { "_id": "104781320", "fullname": "Christina Ntakou", "imgUrl": "https://files.monday.com/use1/photos/104781320/thumb/104781320-user_photo_initials_2026_06_03_12_34_29.png?1780490069" },
            { "_id": "105133741", "fullname": "Madalina Sorescu", "imgUrl": "https://files.monday.com/use1/photos/105133741/thumb/105133741-user_photo_initials_2026_06_09_08_53_50.png?1780995230" },
            { "_id": "104007466", "fullname": "Ismail Osmanov", "imgUrl": "https://files.monday.com/use1/photos/104007466/thumb/104007466-user_photo_initials_2026_05_20_14_20_52.png?1779286852" },
            { "_id": "29806485", "fullname": "Ariane Millo", "imgUrl": "https://files.monday.com/use1/photos/29806485/thumb/29806485-user_photo_2023_05_30_11_41_57.png?1685446917" },
            { "_id": "104769303", "fullname": "Alex Nacul", "imgUrl": "https://files.monday.com/use1/photos/104769303/thumb/104769303-user_photo_initials_2026_06_03_09_29_49.png?1780478989" }
        ],
        "groups": [],
        "activities": [],
        "cmpsOrder": ["status-picker", "member-picker", "date-picker", 'priority-picker', 'updated-picker'],
        "description": "",
        "cmpsOption": ["status-picker", "member-picker", "date-picker", 'priority-picker', 'number-picker', 'file-picker', 'updated-picker']
    }
}


